import React, { useState } from 'react';
import { closeKPIMonth, loadKPIFromIiko, reopenKPIMonth } from '../../services/api';
import { MONTHS, money } from './kpiFormat';

const Chevron = ({ dir }) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {dir === 'left' ? <path d="M15 6l-6 6 6 6" /> : <path d="M9 6l6 6-6 6" />}
  </svg>
);

const shift = ({ year, month }, d) => {
  const m = month - 1 + d;
  return { year: year + Math.floor(m / 12), month: ((m % 12) + 12) % 12 + 1 };
};

const plural = (n, one, few, many) => {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
  return many;
};

/* Шаги месяца — чек-лист управляющего. Каждый шаг сам говорит, что
   осталось сделать и где это сделать. */
const buildSteps = (data) => {
  const c = data.checklist;
  const closed = data.status === 'closed';
  const manualCount = c.manual_missing.reduce((s, m) => s + m.employees.length, 0);
  const total = data.totals.employees;
  return [
    {
      key: 'goals', tab: 'goals', title: 'Цели месяца',
      done: closed || (c.goals.length === 0 && total > 0),
      text: total === 0 ? 'Добавьте сотрудников в «Должностях»'
        : c.goals.length ? `Нужно поправить: ${c.goals.map(g => g.name).join(', ')}` : 'Заданы по всем должностям',
    },
    {
      key: 'iiko', tab: 'roles', title: 'Факт из iiko',
      done: closed || (c.iiko_missing.length === 0 && total > 0),
      // После загрузки «не хватает» значит, что в iiko этого нет (например, не
      // было инвентаризации) — тогда подсказываем внести вручную.
      text: c.iiko_missing.length
        ? `${c.iiko_loaded ? 'Нет в iiko, внесите вручную' : 'Не загружено'}: ${c.iiko_missing.slice(0, 2).map(m => m.name.toLowerCase()).join(', ')}${c.iiko_missing.length > 2 ? '…' : ''}`
        : 'Выручка, продажи, списания загружены',
    },
    {
      key: 'manual', tab: 'roles', title: 'Ручные показатели',
      done: closed || (c.manual_missing.length === 0 && total > 0),
      text: c.manual_missing.length
        ? `Не внесено ${manualCount} ${plural(manualCount, 'значение', 'значения', 'значений')}: ${c.manual_missing.slice(0, 2).map(m => m.name.toLowerCase()).join(', ')}${c.manual_missing.length > 2 ? '…' : ''}`
        : 'Жалобы, сервис, чек-листы внесены',
    },
    {
      key: 'check', tab: 'overview', title: 'Проверка итогов',
      done: closed || (data.totals.complete === total && total > 0),
      text: `Посчитано ${data.totals.complete} из ${total}, фонд ${money(data.totals.fund)}`,
    },
    {
      key: 'close', tab: 'accounting', title: 'Закрыть месяц',
      done: closed,
      text: closed ? 'Утверждено — ведомость у бухгалтера' : 'Фиксирует премии для бухгалтера',
    },
  ];
};

const KpiMonthHeader = ({ period, onPeriod, data, isAdmin, onData, run, notify, onGoTab, onHelp }) => {
  const [busy, setBusy] = useState(null);
  const [notes, setNotes] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const closed = data?.status === 'closed';

  const loadIiko = async () => {
    setBusy('iiko');
    setNotes([]);
    try {
      const res = await loadKPIFromIiko(period.year, period.month);
      onData(res.month);
      setNotes(res.notes || []);
      notify('ok', 'Факт из iiko загружен');
    } catch (e) {
      notify('err', e.response?.data?.error || 'iiko не ответил');
    } finally {
      setBusy(null);
    }
  };

  const close = async (force = false) => {
    setBusy('close');
    try {
      const res = await closeKPIMonth(period.year, period.month, force);
      onData(res);
      setConfirm(null);
      notify('ok', 'Месяц закрыт — премии утверждены');
    } catch (e) {
      const d = e.response?.data;
      if (d?.code === 'incomplete') setConfirm({ employees: d.employees });
      else if (d?.code === 'goals') { notify('err', 'Сначала поправьте цели'); onGoTab('goals'); }
      else notify('err', d?.error || 'Не получилось закрыть месяц');
    } finally {
      setBusy(null);
    }
  };

  const reopen = async () => {
    if (!window.confirm('Открыть месяц заново? Бухгалтер увидит, что ведомость снова черновик.')) return;
    await run(() => reopenKPIMonth(period.year, period.month), 'Месяц открыт для правок');
  };

  const steps = data ? buildSteps(data) : [];
  // В закрытом месяце подсказки «что осталось» уже не нужны.
  if (closed) steps.slice(1, 4).forEach(s => { s.text = s.key === 'check' ? `Фонд ${money(data.totals.fund)}` : 'Зафиксировано'; });
  const next = steps.find(s => !s.done);

  return (
    <section className="kpi2-head">
      <div className="kpi2-head-top">
        <div className="kpi2-title">
          <h1>KPI и премии</h1>
          <p>Цели, факт и премии сотрудников за месяц</p>
        </div>

        <div className="kpi2-period">
          <button type="button" className="ui-icon-btn" aria-label="Предыдущий месяц"
            onClick={() => onPeriod(shift(period, -1))}><Chevron dir="left" /></button>
          <div className="kpi2-period-label">
            <strong>{MONTHS[period.month - 1]} {period.year}</strong>
            {data && (
              <span className={`kpi2-status ${closed ? 'is-closed' : 'is-draft'}`}>
                {closed ? 'Закрыт' : 'Черновик'}
              </span>
            )}
          </div>
          <button type="button" className="ui-icon-btn" aria-label="Следующий месяц"
            onClick={() => onPeriod(shift(period, 1))}><Chevron dir="right" /></button>
        </div>

        <div className="kpi2-head-actions">
          <button type="button" className="ui-btn-ghost" onClick={onHelp}>Как работает KPI</button>
          {!closed && (
            <button type="button" className="ui-btn-ghost" onClick={loadIiko} disabled={!!busy}>
              {busy === 'iiko' ? 'Загружаем из iiko…' : 'Загрузить из iiko'}
            </button>
          )}
          {!closed && (
            <button type="button" className="ui-btn-primary" onClick={() => close(false)} disabled={!!busy}>
              {busy === 'close' ? 'Закрываем…' : 'Закрыть месяц'}
            </button>
          )}
          {closed && isAdmin && (
            <button type="button" className="ui-btn-ghost" onClick={reopen}>Открыть заново</button>
          )}
        </div>
      </div>

      {data && (
        <ol className="kpi2-steps">
          {steps.map((s, i) => (
            <li key={s.key} className={`kpi2-step ${s.done ? 'is-done' : ''} ${next?.key === s.key ? 'is-next' : ''}`}>
              <button type="button" onClick={() => onGoTab(s.tab)}>
                <span className="kpi2-step-num" aria-hidden="true">{s.done ? '✓' : i + 1}</span>
                <span className="kpi2-step-body">
                  <span className="kpi2-step-title">{s.title}</span>
                  <span className="kpi2-step-text">{s.text}</span>
                </span>
              </button>
            </li>
          ))}
        </ol>
      )}

      {closed && data.closed_at && (
        <div className="kpi2-note">
          Месяц закрыт {new Date(data.closed_at).toLocaleDateString('ru-RU')}
          {data.closed_by_name ? `, ${data.closed_by_name}` : ''}. Цифры зафиксированы и не меняются при смене целей.
        </div>
      )}

      {notes.length > 0 && (
        <div className="kpi2-note is-warn">
          {notes.map(n => <div key={n}>{n}</div>)}
        </div>
      )}

      {confirm && (
        <div className="ui-modal" onClick={() => setConfirm(null)}>
          <div className="ui-modal-box wide" onClick={e => e.stopPropagation()}>
            <div className="ui-modal-head">
              <h2>Не у всех заполнен факт</h2>
              <button className="ui-icon-btn" onClick={() => setConfirm(null)} aria-label="Закрыть">×</button>
            </div>
            <p className="kpi2-muted">
              У этих сотрудников внесены не все показатели, поэтому итоговый балл не посчитан.
              Если закрыть месяц сейчас, их премия будет 0 ₽:
            </p>
            <ul className="kpi2-plain-list">
              {confirm.employees.map(n => <li key={n}>{n}</li>)}
            </ul>
            <div className="ui-modal-actions">
              <button className="ui-btn-ghost" onClick={() => { setConfirm(null); onGoTab('roles'); }}>Дозаполнить</button>
              <button className="ui-btn-danger" onClick={() => close(true)} disabled={!!busy}>Закрыть с нулями</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default KpiMonthHeader;
